import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Receipt, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const STATUS_STYLES = {
  draft: "bg-[#f5f6f6] text-[#3f4b52] border-[#cfd7db]",
  issued: "bg-[#ecfdf5] text-[#065f46] border-[#10b981]",
  voided: "bg-[#fef2f2] text-[#7f1d1d] border-[#dc2626]",
};

const PAYMENT_STYLES = {
  paid: "bg-[#ecfdf5] text-[#065f46] border-[#10b981]",
  part_paid: "bg-[#fef3c7] text-[#78350f] border-[#f59e0b]",
  unpaid: "bg-[#f5f6f6] text-[#3f4b52] border-[#cfd7db]",
  overdue: "bg-[#fef2f2] text-[#7f1d1d] border-[#dc2626]",
};

const money = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const AdminInvoices = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listInvoices({
        ...(q ? { query: q } : {}),
        ...(status && status !== "all" ? { status } : {}),
      });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Standalone / bulk invoices</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">Invoices</h1>
          <p className="text-[#3f4b52] mt-1 text-sm max-w-2xl">
            For monthly/bulk billing without a full job card. All numbers pull from the same
            <b> INV####</b> sequence as job invoices — never a duplicate.
          </p>
        </div>
        <Link
          to="/admin/invoices/new"
          data-testid="new-invoice-btn"
          className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-11 rounded-md inline-flex items-center gap-2 hover:brightness-110"
        >
          <Plus size={14} /> New Invoice
        </Link>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search invoice no. / customer / reference…"
            className="w-full pl-9 pr-3 h-10 border border-[#cfd7db] rounded-md text-sm"
            data-testid="invoices-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white"
          data-testid="invoices-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="voided">Voided</option>
        </select>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#111619] text-white text-left">
            <tr>
              <Th>Invoice No.</Th>
              <Th>Date</Th>
              <Th>Customer</Th>
              <Th>Reference</Th>
              <Th>Lines</Th>
              <Th className="text-right">Total</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#eef2f4]">
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-[#3f4b52]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#3f4b52]" data-testid="invoices-empty">
                <Receipt size={20} className="inline-block mr-2 opacity-40" />
                No invoices yet — click <b>New Invoice</b> to create one.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#f5f6f6]" data-testid={`invoice-row-${r.id}`}>
                <Td className="font-mono font-bold text-[#f28a16]">{r.invoice_number || "—"}</Td>
                <Td>{(r.date || "").slice(0, 10)}</Td>
                <Td className="font-bold text-[#111619]">
                  {r.customer_name || "—"}
                  {r.customer_code && <span className="ml-2 text-xs font-display uppercase tracking-widest text-[#3f4b52]">{r.customer_code}</span>}
                </Td>
                <Td className="text-[#3f4b52]">{r.reference || "—"}</Td>
                <Td>{(r.lines || []).length}</Td>
                <Td className="text-right font-bold">{money(r.total)}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    <span className={`inline-block border px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                      {r.status}
                    </span>
                    {r.status === "issued" && r.payment_status && (
                      <span className={`inline-block border px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${PAYMENT_STYLES[r.payment_status] || PAYMENT_STYLES.unpaid}`}
                        data-testid={`payment-pill-${r.id}`}
                      >
                        {String(r.payment_status).replace("_", " ")}
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="text-right">
                  <Link to={`/admin/invoices/${r.id}`} className="text-[#f28a16] hover:underline inline-flex items-center gap-1 text-xs" data-testid={`open-invoice-${r.id}`}>
                    Open <ExternalLink size={11} />
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 font-display uppercase tracking-widest text-xs ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2.5 ${className}`}>{children}</td>
);

export default AdminInvoices;
